/**
 * TeaserProfile Component
 *
 * Displays team member/profile information with image, name, position, and description.
 * Designed for `team_member` content type in Plone.
 *
 * Features:
 * - Responsive width configuration per viewport
 * - Uses Plone scale "profile" (500x500) for images
 * - Supports custom className for styling
 *
 * @module components/Molecules/TeaserProfile
 * @see https://gitlab.earth.bsc.es/ess/essvoltocomponents/-/issues/8
 */
import React from 'react';
import cx from 'classnames';
import { RichText } from 'volto-essvoltocomponents/components/Atoms';
import './TeaserProfile.scss';

export interface TeaserProfileProps {
  /** Image source URL - should use Plone scale "profile" (500x500) */
  imageSrc: string;
  /** Alt text for the image */
  imageAlt?: string;
  /** Person's name */
  name: string;
  /** Job position/role */
  position: string;
  /** Optional description/bio */
  description?: string;
  /** Additional CSS classes */
  className?: string;
  /** Width configuration - can be a fixed value or responsive object */
  width?:
    | string
    | {
        xs?: string;
        sm?: string;
        md?: string;
        lg?: string;
        xl?: string;
      };
  /** URL to link to (optional) */
  href?: string;
  /** Click handler (optional) */
  onClick?: () => void;
  /** Border color (optional) */
  borderColor?: string;
  /** Background color for the card (optional) */
  backgroundColor?: string;
  /** Text/foreground color (optional) */
  foregroundColor?: string;
}

/**
 * TeaserProfile Component
 *
 * Displays profile/team member information in a card format.
 * Previously named TeamMember, renamed for consistency with other Teaser components.
 */
const TeaserProfile: React.FC<TeaserProfileProps> = ({
  imageSrc,
  imageAlt = '',
  name,
  position,
  description,
  className,
  width,
  href,
  onClick,
  borderColor,
  backgroundColor,
  foregroundColor,
}) => {
  // Calculate style based on props
  const getStyle = (): React.CSSProperties => {
    const style: React.CSSProperties = {};

    // Width configuration
    if (width) {
      if (typeof width === 'string') {
        style.width = width;
        style.maxWidth = width;
      } else {
        // For responsive width, we use CSS custom properties
        // The actual responsive behavior is handled via CSS
        style['--teaser-profile-width-xs' as any] = width.xs || '100%';
        style['--teaser-profile-width-sm' as any] = width.sm || width.xs || '100%';
        style['--teaser-profile-width-md' as any] = width.md || width.sm || width.xs || '300px';
        style['--teaser-profile-width-lg' as any] = width.lg || width.md || width.sm || '300px';
        style['--teaser-profile-width-xl' as any] = width.xl || width.lg || width.md || '300px';
      }
    }

    // Color customization
    if (borderColor) {
      style.borderColor = borderColor;
    }
    if (backgroundColor) {
      style.backgroundColor = backgroundColor;
    }
    if (foregroundColor) {
      style['--teaser-profile-foreground-color' as any] = foregroundColor;
    }

    return style;
  };

  const cardClasses = cx('teaser-profile', className);

  const content = (
    <>
      <div className="teaser-profile__image">
        <img
          src={imageSrc}
          alt={imageAlt || `${name} profile photo`}
          loading="lazy"
        />
      </div>

      <div className="teaser-profile__content">
        <div className="teaser-profile__info">
          <h3 className="teaser-profile__name">{name}</h3>
          <RichText className="teaser-profile__position" content={position}/>
        </div>

        {description && (
          <RichText className="teaser-profile__description" content={description}/>
        )}
      </div>
    </>
  );

  // If href is provided, wrap in anchor tag
  if (href) {
    return (
      <a
        href={href}
        className={cardClasses}
        style={getStyle()}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  // If onClick is provided without href, make it a button-like div
  if (onClick) {
    return (
      <div
        className={cardClasses}
        style={getStyle()}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick();
          }
        }}
      >
        {content}
      </div>
    );
  }

  // Default: static card
  return (
    <div className={cardClasses} style={getStyle()}>
      {content}
    </div>
  );
};

export default TeaserProfile;